import { useRef, type RefObject } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent, ScrollView } from 'react-native';
import Dimens from 'src/core/utils/Dimens';
import { clamp } from 'src/core/utils/Numbers';
import { FIRST_PAGE } from 'src/presentation/components/molecules/CarouselPagination';

export const CAROUSEL_CARD_GAP = Dimens.gap16;

const PAGE_TURN_THRESHOLD = 0.8;
const ANIMATED_SCROLL_MS = 600;

const strideForCard = (cardWidth: number): number => cardWidth + CAROUSEL_CARD_GAP;

const pageFromScroll = (scrollLeft: number, cardWidth: number, pageCount: number): number => {
	const pagesScrolled = scrollLeft / strideForCard(cardWidth) + (1 - PAGE_TURN_THRESHOLD);

	return clamp(Math.floor(pagesScrolled) + FIRST_PAGE, FIRST_PAGE, pageCount);
};

const offsetForPage = (page: number, cardWidth: number): number => (page - FIRST_PAGE) * strideForCard(cardWidth);

const pageFromScrollEvent = (
	event: NativeSyntheticEvent<NativeScrollEvent>,
	cardWidth: number,
	pageCount: number,
): number => {
	const layoutWidth = Math.floor(event.nativeEvent.layoutMeasurement.width);
	const scrollLeft = Math.floor(event.nativeEvent.contentOffset.x);
	const scrollWidth = Math.floor(event.nativeEvent.contentSize.width);

	if (layoutWidth <= scrollWidth && layoutWidth + scrollLeft === scrollWidth) {
		return pageCount;
	}

	return pageFromScroll(scrollLeft, cardWidth, pageCount);
};

interface ScrollHandlers {
	onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
	onScrollBeginDrag: () => void;
	onScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
	onMomentumScrollEnd: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

interface CarouselPager {
	ref: RefObject<ScrollView | null>;
	goToPage: (page: number) => void;
	snapInterval: number;
	scrollHandlers: ScrollHandlers;
}

export function useCarouselPager(cardWidth: number, pageCount: number, setPage: (page: number) => void): CarouselPager {
	const ref = useRef<ScrollView>(null);
	const animatingUntil = useRef(0);

	const goToPage = (page: number) => {
		setPage(page);
		animatingUntil.current = Date.now() + ANIMATED_SCROLL_MS;
		ref.current?.scrollTo({ x: offsetForPage(page, cardWidth), animated: true });
	};

	const commitPage = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
		if (Date.now() < animatingUntil.current) {
			return;
		}

		setPage(pageFromScrollEvent(event, cardWidth, pageCount));
	};

	const scrollHandlers: ScrollHandlers = {
		onScrollBeginDrag: () => {
			animatingUntil.current = 0;
		},
		onScroll: commitPage,
		onScrollEndDrag: commitPage,
		onMomentumScrollEnd: commitPage,
	};

	return { ref, goToPage, snapInterval: strideForCard(cardWidth), scrollHandlers };
}

export default useCarouselPager;
